import { createContext, useContext, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QuoteCtx = createContext(null);
export const useQuote = () => useContext(QuoteCtx);

const SERVIZI = [
  { v: "trasloco", l: "Trasloco" },
  { v: "moto", l: "Moto / Scooter" },
  { v: "mobilio", l: "Mobilio" },
  { v: "merci", l: "Merci" },
  { v: "trasporto", l: "Trasporto nazionale" },
  { v: "deposito", l: "Deposito merci" },
  { v: "autoscala", l: "Autoscala & piattaforme" },
  { v: "business", l: "AXAL Business" },
  { v: "altro", l: "Altro" },
];

const empty = { nome: "", telefono: "", email: "", partenza: "", destinazione: "", servizio: "", messaggio: "" };

export const QuoteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const openQuote = (preset = {}) => { setForm({ ...empty, ...preset }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.email) {
      toast.error("Compila nome, telefono ed email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setOpen(false);
      setForm(empty);
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full h-12 px-4 rounded-xl bg-zinc-50 border border-zinc-200 text-[15px] outline-none transition-colors focus:border-[#FF5A00] focus:bg-white placeholder:text-zinc-400";

  return (
    <QuoteCtx.Provider value={{ openQuote }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-8 overflow-y-auto"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="quote-dialog-overlay"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="relative w-full max-w-lg bg-white rounded-[1.5rem] shadow-2xl my-auto"
              initial={{ y: 40, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <button onClick={() => setOpen(false)} data-testid="quote-close-button"
                className="absolute right-5 top-5 text-zinc-400 hover:text-black transition-colors">
                <X size={22} />
              </button>
              <div className="p-7 md:p-9">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5A00]">Preventivo gratuito</p>
                <h3 className="text-2xl md:text-3xl font-extrabold mt-2 tracking-tight">Richiedi un preventivo</h3>
                <p className="text-zinc-500 mt-1 text-sm">Rispondiamo di norma entro poche ore.</p>
                <form onSubmit={submit} className="mt-6 grid grid-cols-2 gap-3">
                  <input className={input} placeholder="Nome e cognome *" value={form.nome} onChange={set("nome")} data-testid="quote-nome" />
                  <input className={input} placeholder="Telefono *" value={form.telefono} onChange={set("telefono")} data-testid="quote-telefono" />
                  <input className={`${input} col-span-2`} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="quote-email" />
                  <input className={input} placeholder="Partenza (Città / CAP)" value={form.partenza} onChange={set("partenza")} data-testid="quote-partenza" />
                  <input className={input} placeholder="Destinazione (Città / CAP)" value={form.destinazione} onChange={set("destinazione")} data-testid="quote-destinazione" />
                  <select className={`${input} col-span-2 text-zinc-600`} value={form.servizio} onChange={set("servizio")} data-testid="quote-servizio">
                    <option value="">Cosa devi trasportare?</option>
                    {SERVIZI.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                  <textarea className={`${input} col-span-2 h-24 py-3 resize-none`} placeholder="Messaggio (facoltativo)" value={form.messaggio} onChange={set("messaggio")} data-testid="quote-messaggio" />
                  <button type="submit" disabled={loading} data-testid="quote-submit"
                    className="col-span-2 h-13 py-4 rounded-xl bg-[#FF5A00] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04F00] transition-colors disabled:opacity-70">
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <>Invia richiesta <ArrowRight size={18} /></>}
                  </button>
                </form>
                <p className="text-center text-xs text-zinc-400 mt-4">
                  Oppure chiamaci al <a href={`tel:${CONTACT.phoneRaw}`} className="font-semibold text-zinc-600">{CONTACT.phoneDisplay}</a> o al <a href={`tel:${CONTACT.landlineRaw}`} className="font-semibold text-zinc-600">{CONTACT.landlineDisplay}</a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteCtx.Provider>
  );
};
